import React from 'react';

interface AvailabilityTemplate {
  day_of_week: number;
  start_time: string;
  end_time: string;
  slot_order: number;
}

interface AvailabilityDisplayProps {
  templates: AvailabilityTemplate[];
}

export const AvailabilityDisplay: React.FC<AvailabilityDisplayProps> = ({
  templates,
}) => {
  const getDayAbbreviation = (dayOfWeek: number) => {
    const dayAbbreviations: { [key: number]: string } = {
      1: 'จ',   // จันทร์
      2: 'อ',   // อังคาร
      3: 'พ',   // พุธ
      4: 'พฤ',  // พฤหัสบดี
      5: 'ศ',   // ศุกร์
      6: 'ส',   // เสาร์
      7: 'อา'   // อาทิตย์
    };
    return dayAbbreviations[dayOfWeek] || dayOfWeek.toString();
  };

  const formatTime = (timeString: string) => {
    const time = new Date(`2000-01-01T${timeString}`);
    return time.toLocaleTimeString('th-TH', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const formatAvailability = () => {
    // Group by day of week
    const groupedByDay = templates.reduce((acc, template) => {
      const day = template.day_of_week;
      if (!acc[day]) {
        acc[day] = [];
      }
      acc[day].push(template);
      return acc;
    }, {} as Record<number, AvailabilityTemplate[]>);

    // Format each day's schedule
    const daySchedules = Object.entries(groupedByDay)
      .sort(([a], [b]) => {
        const dayA = parseInt(a);
        const dayB = parseInt(b);
        // Sort with Sunday (7) appearing after Saturday (6)
        const sortA = dayA === 7 ? 0 : dayA;
        const sortB = dayB === 7 ? 0 : dayB;
        return sortA - sortB;
      })
      .map(([dayOfWeek, dayTemplates]) => {
        const dayAbb = getDayAbbreviation(parseInt(dayOfWeek));
        
        // Sort templates by start time
        const sortedTemplates = dayTemplates.sort((a, b) => 
          a.start_time.localeCompare(b.start_time)
        );

        // Group consecutive time slots
        const timeRanges: string[] = [];
        let currentStart = sortedTemplates[0].start_time;
        let currentEnd = sortedTemplates[0].end_time;

        for (let i = 1; i < sortedTemplates.length; i++) {
          const template = sortedTemplates[i];
          // Check if this slot is consecutive to the previous one
          if (template.start_time === currentEnd) {
            currentEnd = template.end_time;
          } else {
            // Add the current range and start a new one
            timeRanges.push(`${formatTime(currentStart)} - ${formatTime(currentEnd)}`);
            currentStart = template.start_time;
            currentEnd = template.end_time;
          }
        }
        
        // Add the last range
        timeRanges.push(`${formatTime(currentStart)} - ${formatTime(currentEnd)}`);

        return `${dayAbb} ${timeRanges.join(', ')}`;
      });

    return daySchedules.join('\n');
  };

  const formattedAvailability = formatAvailability();

  return (
    <div className="text-xs">
      {formattedAvailability === '-' ? (
        <span className="text-muted-foreground">-</span>
      ) : (
        <div className="whitespace-pre-line">
          {formattedAvailability}
        </div>
      )}
    </div>
  );
};